import {
  useEffect,
  useReducer,
} from 'react';
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import superagent from 'superagent';

import {
  Eye,
  EyeCrossed,
} from '../../assets/js/svg/gui';
import {
  Button,
  Column,
  ConfirmPopup,
  Row,
  TextInput,
} from '../../components';
import SupportLink from '../../components/SupportLink';
import {
  useAuth,
  useTownAssets,
} from '../../stores';
import {
  config,
  displayJson,
  logError,
  passwordEditChecks,
} from '../../utils';

const initialState = {
  password: '',
  confirmPassword: '',
  showPassword: false,
  current: 'init',
  redirectTimer: 0,
  warnMessage: undefined,
  valid: false,
};

const stateReducer = (state, action) => {
  let newState;
  let checks;

  switch (action.type) {
    case 'ready':
      return ({
        ...state,
        current: 'input',
      });

    case 'input':
      // console.log(`RecoverPage stateReducer action=${displayJson(action)}`);
      newState = { ...state };
      newState[action.id] = action.value;

      checks = passwordEditChecks(
        action.regex,
        newState.password,
        newState.confirmPassword,
      );

      newState.valid = false;
      if (checks.min) newState.warnMessage = `Le mot de passe doit contenir au moins ${checks.minReq} caractères`;
      else if (checks.max) newState.warnMessage = `Le mot de passe doit contenir au plus ${checks.maxReq} caractères`;
      else if (checks.missingSpecial) newState.warnMessage = `Le mot de passe doit contenir au moins ${checks.minSpecialChars} caractères spéciaux`;
      else if (checks.forbidden && checks.forbidden.length) {
        newState.warnMessage = `Le mot de passe ne peut contenir ces caractères: ${checks.forbidden.reduce(
          (prev, current) => {
            if (!prev) return `"${current}"`;

            return `${prev}, "${current}"`;
          },
          null,
        )}`;
      } else if (!checks.match) newState.warnMessage = 'Les mots de passe doivent être identiques';
      else {
        newState.warnMessage = undefined;
        newState.valid = true;
      }

      return newState;

    case 'click':
      switch (action.id) {
        case 'showPassword':
          return ({
            ...state,
            showPassword: !state.showPassword,
          });

        case 'recover':
          return ({
            ...state,
            current: 'confirm',
          });

        default:
          logError(
            `RecoverPage stateReducer unknown id action=${displayJson(action)}`,
            'RecoverPage.reducer.unknownClickId',
            { actionId: action?.id, actionType: action?.type },
          );
          return state;
      }

    case 'cancel':
      return ({
        ...state,
        current: 'input',
      });

    case 'req':
      return ({
        ...state,
        current: 'pending',
      });

    case 'fail':
      return ({
        ...state,
        current: 'input',
        warnMessage: 'échec',
      });

    case 'done':
      return ({
        ...state,
        current: 'success',
        redirectTimer: 8,
      });

    case 'expired':
      return ({
        ...state,
        current: 'expired',
      });

    case 'countDown':
      return ({
        ...state,
        redirectTimer: (state.redirectTimer) ? (state.redirectTimer - 1) : 0,
      });

    default:
      logError(
        `RecoverPage stateReducer unknown type action=${displayJson(action)}`,
        'RecoverPage.reducer.unknownAction',
        { actionType: action?.type },
      );
      return state;
  }
};

function RecoverPage() {
  const { auth } = useAuth();
  const { assets: { translate } } = useTownAssets();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const [searchParams] = useSearchParams();

  const token = searchParams.get('t');

  useEffect(
    () => {
      if (auth.server
        && auth.server.regex
        && state.current === 'init'
      ) dispatch({ type: 'ready' });
    },
    [auth.server, state],
  );

  useEffect(
    () => {
      if (state.redirectTimer) {
        const timer = setTimeout(
          () => {
            dispatch({ type: 'countDown' });
          },
          1000,
        );

        return () => {
          clearTimeout(timer);
        };
      }

      return undefined;
    },
    [state.redirectTimer],
  );

  useEffect(
    () => {
      // console.log(`state=${displayJson(state)}`);
      if ((state.current === 'success') && (state.redirectTimer === 0)) {
        // Use void to make it clear we do not want to wait this async function:
        void navigate('/login');
      }
    },
    [state, navigate],
  );

  const onChange = (event) => {
    dispatch({
      type: 'input',
      id: event.currentTarget.id,
      value: event.currentTarget.value,
      regex: auth.server.regex,
    });
  };

  const handleInputKeyDown = async (event) => {
    if (event.keyCode === 13) {
      // if (!(auth.server.reqPending || (username === '') || (password === ''))) {
      //   connect();
      // }
    }
  };

  const onClick = (event) => {
    dispatch({ type: 'click', id: event.currentTarget.id });
  };

  const stateEvent = (event) => {
    dispatch({ type: event });
  };

  // const onClickRecovery = () => {
  //   setConfirmRecovery(true);
  // };

  const recover = async () => {
    dispatch({ type: 'req' });
    console.log('recover');

    const url = config.town.back.urlPrefix
              + config.town.back.auth.recoverEnd;

    const payload = {
      password: state.password,
    };
    let rslt;

    try {
      // console.log(`recover at ${url}`);
      rslt = await superagent
        .post(url)
        .set('Content-Type', 'application/json') // headers
        .set('Authorization', `Bearer ${token}`)
        .send(payload);
    } catch (err) {
      logError(
        `recover err=${err}`,
        'RecoverPage.recover.error',
        {
          err: err?.message,
          ...(err?.status && { status: err.status }),
          url,
        },
      );
      dispatch({ type: 'fail' });
      return;
    }

    if (rslt?.body?.rslt?.data?.expired) {
      dispatch({ type: 'expired' });
      return;
    }

    dispatch({ type: 'done' });
    console.log(`rslt=${displayJson(rslt)}`);
  };

  if (!token) {
    return (
      <main
        className="h-screen w-screen max-h-screen max-w-screen p-2 absolute l-0 overflow-hidden flex flex-col items-center justify-center font-light text-start text-base bg-white"
      >
        <Column>
          <img
            src="/images/png/logo.png"
            alt="paxity"
            className="w-24 mb-10"
          />

          <span className="mb-4">Lien non reconnu</span>
          <SupportLink />
        </Column>
      </main>
    );
  }

  if (state.current === 'success') {
    return (
      <main
        className="h-screen w-screen max-h-screen max-w-screen p-2 absolute l-0 overflow-hidden flex flex-col items-center justify-center font-light text-start text-base bg-white"
      >
        <Column>
          <img
            src="/images/png/logo.png"
            alt="paxity"
            className="w-24 mb-10"
          />

          <span className="mb-4">Mot de passe modifié</span>

          <span className="mb-4">
            Redirection vers la
            <a href="/login" className="text-pxTxtLink"> page de connexion</a>
            {` dans ${state.redirectTimer}s.`}
          </span>
        </Column>
      </main>
    );
  }

  if (state.current === 'expired') {
    return (
      <main
        className="h-screen w-screen max-h-screen max-w-screen p-2 absolute l-0 overflow-hidden flex flex-col items-center justify-center font-light text-start text-base bg-white"
      >
        <Column>
          <img
            src="/images/png/logo.png"
            alt="paxity"
            className="w-24 mb-10"
          />

          <span className="mb-4 text-center">
            {(() => {
              const [before, after] = translate('recoverLinkExpired', 'statements').split('%{link}');
              return (
                <>
                  {before}
                  <a href="/login" className="text-pxTxtLink">
                    {translate('loginPage', undefined, undefined, false)}
                  </a>
                  {after}
                </>
              );
            })()}
          </span>

          <SupportLink />
        </Column>
      </main>
    );
  }

  return (
    <main
      className="h-screen w-screen max-h-screen max-w-screen p-2 absolute l-0 overflow-hidden flex flex-col items-center justify-center font-light text-start text-base bg-white"
    >

      <Column>
        <img
          src="/images/png/logo.png"
          alt="paxity"
          className="w-24 mb-10"
        />

        <span className="mb-4">Saisissez un nouveau mot de passe:</span>

        <div className="grid grid-cols-2-auto gap-y-5 gap-x-2 justify-items-center items-center">
          <TextInput
            id="password"
            name="password"
            placeholder="Mot de passe"
            showPassword={state.showPassword}
            fillType="newPassword"
            autoFill
            className="flex-1"
            onChange={onChange}
            value={state.password || ''}
            onKeyDown={handleInputKeyDown}
            focus
          />

          <Button
            id="showPassword"
            aria-label="toggle password visibility"
            onClick={onClick}
            Image={(state.showPassword) ? Eye : EyeCrossed}
            noFrame
            imgColor="#808080"
          />

          <TextInput
            id="confirmPassword"
            placeholder="Confirmation mot de passe"
            showPassword={state.showPassword}
            fillType="newPassword"
            autoFill
            onChange={onChange}
            value={state.confirmPassword || ''}
            onKeyDown={handleInputKeyDown}
          />

          <div />

          <Row gap={6} className="w-full col-span-2">
            {/* <Button
                onClick={handleClearClick}
                disabled={(!!auth.server.reqPending) || ((username === '') && (password === ''))}
                imgSrc="/images/svg/gui/trash.svg"
                criticalColor
              /> */}

            <Button
              id="recover"
              onClick={onClick}
              disabled={!state.valid}
              imgSrc="/images/svg/gui/play.svg"
              baseColor
            />
          </Row>

          <Row className="w-full col-span-2">
            {(state.warnMessage)
              ? (
                  <Row gap={2}>
                    <span className="text-pxUiAlert">{state.warnMessage}</span>
                    {(state.current === 'input' && state.warnMessage === 'échec')
                      ? <SupportLink />
                      : null}
                  </Row>
                )
              : <span>{' '}</span>}
          </Row>
        </div>
      </Column>

      {(state.current === 'confirm')
        ? (
            <ConfirmPopup
              question={[
                'Souhaitez-vous réinitialiser votre mot de passe?',
                'Vous pourrez vous reconnecter avec votre nouveau mot de passe juste après cette procédure.',
              ]}
              // Use void to make it clear we do not want to wait this async function:
              onConfirm={() => { void recover(); }}
              onReject={() => { stateEvent('cancel'); }}
              yes="Oui"
              no="Non"
            />
          )
        : null}
    </main>
  );
}

export default RecoverPage;
